"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Voyez ce qui sort de la fenêtre",
        size: "Taille de la fenêtre",
        system: "Consigne système",
        docs: "Documents joints",
        messages: "Messages échangés",
        used: "occupé",
        free: "libre",
        dropped: "message(s) sorti(s) de la fenêtre",
        droppedNote: "Le modèle répond comme si ces échanges n'avaient jamais eu lieu.",
        fits: "Tout tient dans la fenêtre.",
        tokens: "jetons",
        legendSystem: "Consigne",
        legendDocs: "Documents",
        legendHistory: "Historique",
        legendAnswer: "Réponse à venir",
        note: "Un message coûte environ 300 jetons. La place réservée à la réponse est retirée du total disponible.",
    },
    en: {
        title: "See what falls out of the window",
        size: "Window size",
        system: "System instruction",
        docs: "Attached documents",
        messages: "Messages exchanged",
        used: "used",
        free: "free",
        dropped: "message(s) dropped from the window",
        droppedNote: "The model answers as if those exchanges never happened.",
        fits: "Everything fits in the window.",
        tokens: "tokens",
        legendSystem: "Instruction",
        legendDocs: "Documents",
        legendHistory: "History",
        legendAnswer: "Reserved for the answer",
        note: "One message costs around 300 tokens. The room reserved for the answer is taken off the available total.",
    },
};

const SIZES = [8000, 32000, 128000, 200000];
const TOKENS_PER_MESSAGE = 300;
const ANSWER_RESERVE = 1000;

export default function ContextWindowDemo({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [sizeIndex, setSizeIndex] = useState(0);
    const [system, setSystem] = useState(500);
    const [docs, setDocs] = useState(2000);
    const [messages, setMessages] = useState(20);

    const r = useMemo(() => {
        const size = SIZES[sizeIndex];
        const wanted = messages * TOKENS_PER_MESSAGE;
        const room = size - system - docs - ANSWER_RESERVE;

        // L'historique est le seul bloc qui se fait rogner : le plus ancien sort.
        const kept = Math.max(0, Math.min(wanted, room));
        const droppedMessages = Math.ceil((wanted - kept) / TOKENS_PER_MESSAGE);

        const used = system + docs + kept + ANSWER_RESERVE;

        return {
            size,
            kept,
            droppedMessages: Math.max(0, droppedMessages),
            used,
            free: Math.max(0, size - used),
            pct: (v) => `${Math.max(0, (v / size) * 100)}%`,
        };
    }, [sizeIndex, system, docs, messages]);

    const big = (v) =>
        Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

    const field = (label, value, setValue, min, max, step) => (
        <div>
            <div className="flex justify-between items-baseline mb-1.5">
                <label className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                    {label}
                </label>
                <span className="text-sm font-bold tabular-nums dark:text-white">
                    {big(value)}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                className="w-full accent-primary cursor-pointer"
            />
        </div>
    );

    const legend = (color, label, value) => (
        <div className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${color}`} />
            <span className="text-xs text-gray-600 dark:text-lightBlue">
                {label} <span className="tabular-nums">{big(value)}</span>
            </span>
        </div>
    );

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-5">
                <SolarIcon
                    name="siderbar-linear"
                    className="w-5 h-5 text-primary"
                />
                <div className="font-bold dark:text-white text-base leading-snug">
                    {t.title}
                </div>
            </div>

            <div className="mb-5">
                <div className="text-sm text-gray-600 dark:text-lightBlue mb-1.5">
                    {t.size}
                </div>
                <div className="flex gap-1.5">
                    {SIZES.map((s, i) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setSizeIndex(i)}
                            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors tabular-nums ${
                                sizeIndex === i
                                    ? "bg-primary text-white border-primary"
                                    : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-lightBlue hover:border-gray-300 dark:hover:border-white/20"
                            }`}
                        >
                            {s / 1000}k
                        </button>
                    ))}
                </div>
            </div>

            {/* Barre de remplissage : chaque segment est un bloc du contexte */}
            <div className="flex h-9 w-full rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 mb-3">
                <div
                    className="bg-primary/70"
                    style={{ width: r.pct(system) }}
                    title={t.legendSystem}
                />
                <div
                    className="bg-primary/40"
                    style={{ width: r.pct(docs) }}
                    title={t.legendDocs}
                />
                <div
                    className="bg-primary/20"
                    style={{ width: r.pct(r.kept) }}
                    title={t.legendHistory}
                />
                <div
                    className="bg-gray-300/60 dark:bg-white/10"
                    style={{ width: r.pct(ANSWER_RESERVE) }}
                    title={t.legendAnswer}
                />
                <div className="flex-1 bg-gray-100/60 dark:bg-white/[0.02]" />
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-5">
                {legend("bg-primary/70", t.legendSystem, system)}
                {legend("bg-primary/40", t.legendDocs, docs)}
                {legend("bg-primary/20", t.legendHistory, r.kept)}
                {legend("bg-gray-300/60 dark:bg-white/10", t.legendAnswer, ANSWER_RESERVE)}
            </div>

            <div className="grid sm:grid-cols-2 gap-5 items-start">
                <div className="space-y-4">
                    {field(t.system, system, setSystem, 100, 6000, 100)}
                    {field(t.docs, docs, setDocs, 0, 60000, 500)}
                    {field(t.messages, messages, setMessages, 1, 400, 1)}
                </div>

                <div
                    className={`rounded-xl p-5 ${
                        r.droppedMessages > 0
                            ? "bg-red-500/[0.07] dark:bg-red-500/10"
                            : "bg-green-500/[0.07] dark:bg-green-500/10"
                    }`}
                >
                    {r.droppedMessages > 0 ? (
                        <>
                            <div className="text-4xl font-bold text-red-600 dark:text-red-400 tabular-nums mb-1">
                                {big(r.droppedMessages)}
                            </div>
                            <div className="text-sm font-semibold text-red-700 dark:text-red-300 mb-0">
                                {t.dropped}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-lightBlue mt-2 mb-0">
                                {t.droppedNote}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                                {t.fits}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-lightBlue mb-0 tabular-nums">
                                {big(r.free)} {t.tokens} {t.free}
                            </div>
                        </>
                    )}
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0 tabular-nums">
                        {big(r.used)} / {big(r.size)} {t.tokens} {t.used}
                    </div>
                </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 mb-0">
                {t.note}
            </p>
        </div>
    );
}
